import { useCallback, useEffect, useState } from "react";
import { Badge, Box, Button, Card, Divider, IndexTable, InlineGrid, Tabs, Text, TextField, Thumbnail } from "@shopify/polaris";
import { useOutletContext, useSearchParams } from "react-router-dom";
import agent from "../../app/api/agent";
import { AdminPagedResult, AdminTurn14ItemDetails, AdminTurn14ItemSummary, AdminTurn14SyncLog } from "../../app/models/Admin";
import { formatLocalDateTime } from "./utils/dateTime";

type AdminConsoleContext = { token: string; onUnauthorized: () => void };

const pageSize = 50;

export default function AdminTurn14Page() {
  const { token, onUnauthorized } = useOutletContext<AdminConsoleContext>();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedTab = Number(searchParams.get("tab") || "0") || 0;

  const handleTabChange = (nextTab: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("tab", String(nextTab));
    if (nextTab === 0 && !params.get("itemPage")) params.set("itemPage", "1");
    if (nextTab === 1 && !params.get("logPage")) params.set("logPage", "1");
    setSearchParams(params);
  };

  return (
    <Card>
      <div style={{ display: "grid", gap: "0.75rem" }}>
        <Text as="h2" variant="headingMd">Turn14</Text>
        <Tabs tabs={[{ id: "items", content: "Items" }, { id: "logs", content: "Turn14 sync logs" }]} selected={selectedTab} onSelect={handleTabChange} />
        {selectedTab === 0 ? <Turn14ItemsTab token={token} onUnauthorized={onUnauthorized} /> : <Turn14LogsTab token={token} onUnauthorized={onUnauthorized} />}
      </div>
    </Card>
  );
}

function Turn14ItemsTab({ token, onUnauthorized }: AdminConsoleContext) {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, Number(searchParams.get("itemPage") || "1") || 1);
  const initialQuery = searchParams.get("q") || "";
  const [queryInput, setQueryInput] = useState(initialQuery);
  const [query, setQuery] = useState(initialQuery);
  const [items, setItems] = useState<AdminPagedResult<AdminTurn14ItemSummary> | null>(null);
  const [selectedProductId, setSelectedProductId] = useState<string | null>(searchParams.get("productId"));
  const selectedView = searchParams.get("view") === "details" ? "details" : "list";
  const [details, setDetails] = useState<AdminTurn14ItemDetails | null>(null);
  const [loadingItems, setLoadingItems] = useState(false);
  const [loadingDetails, setLoadingDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadItems = useCallback(async () => {
    setLoadingItems(true);
    setError(null);
    try {
      const response = await agent.Admin.turn14Items(token, page, pageSize, query);
      setItems(response);
    } catch {
      setError("Unable to load Turn14 items.");
      onUnauthorized();
    } finally {
      setLoadingItems(false);
    }
  }, [token, page, query, onUnauthorized]);

  const loadDetails = useCallback(async () => {
    if (!selectedProductId) {
      setDetails(null);
      return;
    }

    setLoadingDetails(true);
    setError(null);
    try {
      const response = await agent.Admin.turn14ItemDetails(token, selectedProductId);
      setDetails(response);
    } catch {
      setError("Unable to load Turn14 item details.");
    } finally {
      setLoadingDetails(false);
    }
  }, [token, selectedProductId]);

  useEffect(() => { loadItems(); }, [loadItems]);
  useEffect(() => { loadDetails(); }, [loadDetails]);

  const totalPages = items ? Math.max(1, Math.ceil(items.totalCount / items.pageSize)) : 1;

  const updatePage = (nextPage: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("itemPage", String(nextPage));
    setSearchParams(params);
  };

  const applySearch = () => {
    const trimmed = queryInput.trim();
    setQuery(trimmed);
    const params = new URLSearchParams(searchParams);
    params.set("itemPage", "1");
    params.set("tab", "0");
    if (trimmed) params.set("q", trimmed); else params.delete("q");
    setSearchParams(params);
  };

  const openItemDetails = (productId: string | null) => {
    setSelectedProductId(productId);
    const params = new URLSearchParams(searchParams);
    if (productId) {
      params.set("productId", productId);
      params.set("view", "details");
    } else {
      params.delete("productId");
      params.set("view", "list");
    }
    params.set("tab", "0");
    setSearchParams(params);
  };

  const backToList = () => {
    const params = new URLSearchParams(searchParams);
    params.set("view", "list");
    params.set("tab", "0");
    setSearchParams(params);
  };

  return (
    <div style={{ display: "grid", gap: "0.75rem" }}>
      <div style={{ display: "flex", gap: "0.5rem", alignItems: "end" }}>
        <div style={{ minWidth: 280 }}>
          <TextField label="Keyword / Part number" value={queryInput} onChange={setQueryInput} autoComplete="off" />
        </div>
        <Button variant="primary" onClick={applySearch}>Search</Button>
        <Button onClick={loadItems} loading={loadingItems}>Refresh</Button>
      </div>

      {error ? <Text as="p" tone="critical">{error}</Text> : null}

      {selectedView === "list" ? (
        <>
          {loadingItems ? <Text as="p">Loading items...</Text> : (
            <IndexTable
              resourceName={{ singular: "Turn14 item", plural: "Turn14 items" }}
              itemCount={items?.items.length || 0}
              selectable={false}
              headings={[{ title: "Item" }, { title: "Part #" }, { title: "Brand" }, { title: "Inventory" }, { title: "Special order" }, { title: "Last sync" }]}
            >
              {(items?.items || []).map((item, index) => (
                <IndexTable.Row id={item.productId || String(item.productTableId)} key={item.productTableId} position={index}>
                  <IndexTable.Cell>
                    <Button variant="plain" onClick={() => openItemDetails(item.productId || null)}>{item.productName || item.productId || "Unknown"}</Button>
                  </IndexTable.Cell>
                  <IndexTable.Cell>{item.partNumber || item.mfrPartNumber || "-"}</IndexTable.Cell>
                  <IndexTable.Cell>{item.brand || "-"}</IndexTable.Cell>
                  <IndexTable.Cell>{item.quantity} (OO: {item.quantityOnOrder})</IndexTable.Cell>
                  <IndexTable.Cell>{item.specialOrder ? <Badge tone="attention">Yes</Badge> : <Text as="span" tone="subdued">No</Text>}</IndexTable.Cell>
                  <IndexTable.Cell>{formatLocalDateTime(item.lastSyncAt)}</IndexTable.Cell>
                </IndexTable.Row>
              ))}
            </IndexTable>
          )}

          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Button disabled={page <= 1 || loadingItems} onClick={() => updatePage(page - 1)}>Previous</Button>
            <Text as="span">Page {page} of {totalPages}</Text>
            <Button disabled={page >= totalPages || loadingItems} onClick={() => updatePage(page + 1)}>Next</Button>
          </div>
        </>
      ) : (
        <>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Button onClick={backToList}>← Back to list</Button>
            {selectedProductId ? <Text as="span" tone="subdued">Viewing item {selectedProductId}</Text> : null}
          </div>
          <Divider />
          <ItemDetails details={details} loading={loadingDetails} onOpenItem={openItemDetails} />
        </>
      )}
    </div>
  );
}

function ItemDetails({ details, loading, onOpenItem }: { details: AdminTurn14ItemDetails | null; loading: boolean; onOpenItem: (productId: string | null) => void }) {
  if (loading) return <Text as="p">Loading item details...</Text>;
  if (!details) return <Text as="p" tone="subdued">Select an item to view full details.</Text>;

  const product = details.product;

  return (
    <div style={{ display: "grid", gap: "0.75rem" }}>
      <InlineGrid columns={2} gap="400">
        <Card>
          <div style={{ display: "grid", gap: "0.5rem" }}>
            <Text as="h3" variant="headingMd">{product.productName || product.productId}</Text>
            {product.thumbnail ? <Thumbnail source={product.thumbnail} alt={product.productName || "Item image"} size="large" /> : null}
            <Text as="p">Part #: {product.partNumber || "-"} | Mfr #: {product.mfrPartNumber || "-"}</Text>
            <Text as="p">Brand: {product.brand || details.brand?.name || "-"}</Text>
            <Text as="p">Category: {product.category || "-"} / {product.subcategory || "-"}</Text>
            <Text as="p">Price group: {product.priceGroup || "-"} ({product.priceGroupId || "-"})</Text>
            <Text as="p">Dimensions: {details.dimensions.length === 0 ? "-" : details.dimensions.map((dimension) => `Box ${dimension.boxNumber}: ${dimension.length}L x ${dimension.width}W x ${dimension.height}H, ${dimension.weight}lb`).join(" | ")}</Text>
            <Text as="p">Special order: {details.accountProducts.some(x => x.specialOrder) ? "Yes" : "No"}</Text>
            <Text as="p">Active: {product.active ? "Yes" : "No"} | Clearance: {product.clearanceItem ? "Yes" : "No"}</Text>
          </div>
        </Card>
        <Card>
          <Text as="h3" variant="headingMd">Parent / Child items</Text>
          <Box paddingBlockStart="300">
            <Text as="p" variant="headingSm">Parent items ({details.parentItems.length})</Text>
            {details.parentItems.length === 0 ? <Text as="p" tone="subdued">None</Text> : details.parentItems.map((x, i) => <Text key={`p-${i}`} as="p"><Button variant="plain" onClick={() => onOpenItem(x.productId || null)}>{x.productId || "-"}</Button> · {x.partNumber || "-"} · qty in parent: {x.quantity}</Text>)}
            <Box paddingBlockStart="300">
              <Text as="p" variant="headingSm">Child items ({details.childItems.length})</Text>
              {details.childItems.length === 0 ? <Text as="p" tone="subdued">None</Text> : details.childItems.map((x, i) => <Text key={`c-${i}`} as="p"><Button variant="plain" onClick={() => onOpenItem(x.productId || null)}>{x.productId || "-"}</Button> · {x.partNumber || "-"} · qty: {x.quantity}</Text>)}
            </Box>
          </Box>
        </Card>
      </InlineGrid>

      <Card>
        <Text as="h3" variant="headingMd">Inventory</Text>
        {details.inventories.length === 0 ? <Text as="p" tone="subdued">No inventory rows.</Text> : details.inventories.map((row, i) => (
          <Text as="p" key={i}>Loc {row.locationId || "-"}: qty {row.quantity}, on-order {row.quantityOnOrder}, mfr-stock {row.manufacturerStock}</Text>
        ))}
      </Card>
    </div>
  );
}

function Turn14LogsTab({ token, onUnauthorized }: AdminConsoleContext) {
  const [searchParams, setSearchParams] = useSearchParams();
  const page = Math.max(1, Number(searchParams.get("logPage") || "1") || 1);
  const [result, setResult] = useState<AdminPagedResult<AdminTurn14SyncLog> | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const loadLogs = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await agent.Admin.turn14SyncLogs(token, page, pageSize);
      setResult(response);
    } catch {
      setError("Unable to load Turn14 sync logs.");
      onUnauthorized();
    } finally {
      setLoading(false);
    }
  }, [token, page, onUnauthorized]);

  useEffect(() => { loadLogs(); }, [loadLogs]);
  const totalPages = result ? Math.max(1, Math.ceil(result.totalCount / result.pageSize)) : 1;

  const setPage = (next: number) => {
    const params = new URLSearchParams(searchParams);
    params.set("logPage", String(next));
    params.set("tab", "1");
    setSearchParams(params);
  };

  return (
    <div style={{ display: "grid", gap: "0.75rem" }}>
      <div style={{ display: "flex", justifyContent: "space-between" }}>
        <Text as="p" variant="bodyMd">Turn14 sync logs ordered by In Progress DESC, ID DESC.</Text>
        <Button onClick={loadLogs} loading={loading}>Refresh logs</Button>
      </div>
      {error ? <Text as="p" tone="critical">{error}</Text> : null}
      <IndexTable
        resourceName={{ singular: "Turn14 sync log", plural: "Turn14 sync logs" }}
        itemCount={result?.items.length || 0}
        selectable={false}
        headings={[{ title: "ID" }, { title: "Object" }, { title: "Status" }, { title: "Pages" }, { title: "Count" }, { title: "Started" }, { title: "Ended" }, { title: "Error" }]}
      >
        {(result?.items || []).map((log, index) => (
          <IndexTable.Row id={String(log.id)} key={log.id} position={index}>
            <IndexTable.Cell>{log.id}</IndexTable.Cell>
            <IndexTable.Cell>{log.syncObject || "-"}</IndexTable.Cell>
            <IndexTable.Cell>{log.inProgress ? <Badge tone="attention">In Progress</Badge> : <Badge tone="success">{log.status || "Complete"}</Badge>}</IndexTable.Cell>
            <IndexTable.Cell>{log.pagesSynced || 0} / {log.totalPages || 0}</IndexTable.Cell>
            <IndexTable.Cell>{log.objectCount}</IndexTable.Cell>
            <IndexTable.Cell>{formatLocalDateTime(log.startedAt)}</IndexTable.Cell>
            <IndexTable.Cell>{formatLocalDateTime(log.endedAt)}</IndexTable.Cell>
            <IndexTable.Cell>{log.errorMessage || "-"}</IndexTable.Cell>
          </IndexTable.Row>
        ))}
      </IndexTable>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Button disabled={page <= 1 || loading} onClick={() => setPage(page - 1)}>Previous</Button>
        <Text as="span">Page {page} of {totalPages}</Text>
        <Button disabled={page >= totalPages || loading} onClick={() => setPage(page + 1)}>Next</Button>
      </div>
    </div>
  );
}
